import { defineStore } from 'pinia';
import { computed, reactive } from 'vue';
import { IAllBusinessesForSupplierForecastState } from '../domains/entities/allBusinessesForSupplierForecast';
import { TableContentHeader, TableContentProperty } from '@/corePagesModule/BaseTableContent/interface/tableContentHeaders';
import { i18n } from '@/i18n';
import { DEFAULT_DISPLAY_FORECAST_MONTH, MAX_OF_DISPLAY_FORECAST_MONTH, MIN_OF_DISPLAY_FORECAST_MONTH } from '../constants';
import { initPagingOptions, IPagingOptions } from '@/coreDomainsModule/paging';
import { DateService } from '@/coreDomainsModule/services/dateService';
import { NarrowState } from '@/corePagesModule/BaseTableContent/interface/uiState';
import { BulkUpdateSupplierForecastUseCase } from '../useCases/bulkUpdateSupplierForecastUseCase';
import { IYearMonth } from '@/coreDomainsModule/date';
import { toast } from '@/corePagesModule/components/Toast/ToastManager';
import { FilterContentItem } from '@/corePagesModule/filterModal/filterModal';
import { simpleCopy } from '@/utils/deepCopy';
import { GetAllSupplierForecastUseCase } from '../useCases/getAllSupplierForecastUseCase';
import { SupplierForecastRepository } from '@/coreInfrastructureModule/Repositories/SupplierForecastRepository';
import { DownloadForecastUseCase } from '../useCases/downloadForecastUseCase';
import { saveAs } from 'file-saver';
import { ISupplierForecastExportDataResult, SupplierForecastExportData } from '../domains/entities/supplierForecastExportData';
import { AllBusinessesForSupplierForecastViewModel } from './allBusinessesForSupplierForecastViewModel';
import { HEADER_MODIFY_TABLE_CONTENT_PROPERTY } from '@/corePagesModule/BaseTableContent/constants';
import { SUUPLIER_FORRECAST_ALL_BUSINESS_TABLE_TYPE } from '@/supplierForecastModule/constants';
import { BulkGetSupplierForecastExportData } from '../useCases/bulkGetSupplierForecastExportDataUseCase';
import { useTableFilter } from '@/corePagesModule/composables/useTableFilter';
import { loadPreferences, myPreferences, savePreferences } from '@/appLogic/view/preferences';

export const useAllBusinessesForSupplierForecastStore = defineStore('allBusinessesForSupplierForecastStore', () => {
  const t = i18n.t;

  const viewModel = new AllBusinessesForSupplierForecastViewModel();

  const getAllSupplierForecastUseCase = new GetAllSupplierForecastUseCase();
  const bulkUpdateSupplierForecastUseCase = new BulkUpdateSupplierForecastUseCase();
  const supplierForecastRepository = new SupplierForecastRepository();

  /**
   * 表示する内示の月数の選択肢
   */
  const monthsOptionsDisplayForecastNumber = Array(MAX_OF_DISPLAY_FORECAST_MONTH - MIN_OF_DISPLAY_FORECAST_MONTH + 1)
    .fill(0)
    .map((_, i) => i + MIN_OF_DISPLAY_FORECAST_MONTH);

  const state = reactive<IAllBusinessesForSupplierForecastState>({
    businessTableContents: {
      renderingKey: 0,
      contentHeader: {} as TableContentHeader,
      contentItems: undefined,
      checkedDisplayIds: [],
      loadingState: {
        status: {
          type: 'none',
        },
      },
      pagingOptions: initPagingOptions(30),
      sortKey: undefined,
      isAsc: true,
      filterState: [],
    },
    monthsDisplayForecast: DEFAULT_DISPLAY_FORECAST_MONTH,
    yearMonthInfoCreatedForecast: {
      year: 0,
      month: 0,
    },
    isOpenSelectInputType: undefined,
    coordinateFilterButton: {
      x: 0,
      y: 0,
    },
    downloadForecastModalState: {
      modalState: false,
      period: {
        start: { year: 0, month: 0 },
        end: { year: 0, month: 0 },
      },
      startMonthPickerState: false,
      endSelectInputState: false,
      startInvalidMessageState: false,
      loadingState: {
        status: {
          type: 'none',
        },
      },
    },
    editForecastModalState: {
      modalState: false,
      selectedStaffIdList: [],
    },
  });

  // フィルター情報のComposable
  const { cachedFilterItems, bulkUpdateFilterInfos, getFilterItemsValue, initTableFilter } =
    useTableFilter('supplierForecastAllBusinessTable');

  /**
   * チェックされた項目の取引IDを取得する
   */
  const checkedBusinessIds = computed(() => {
    if (!state.businessTableContents.contentItems) return [];
    return state.businessTableContents.contentItems
      .filter((item) => state.businessTableContents.checkedDisplayIds.includes(item.displayId))
      .map((item) => item.businessId);
  });

  /**
   * stateの初期化
   */
  const $reset = () => {
    state.businessTableContents.renderingKey = 0;
    state.businessTableContents.contentHeader = {} as TableContentHeader;
    state.businessTableContents.contentItems = undefined;
    state.businessTableContents.checkedDisplayIds = [];
    state.businessTableContents.loadingState = {
      status: {
        type: 'none',
      },
    };
    state.businessTableContents.pagingOptions = initPagingOptions(30);
    state.businessTableContents.sortKey = undefined;
    state.businessTableContents.isAsc = true;
    state.businessTableContents.filterState = [];
    state.monthsDisplayForecast = DEFAULT_DISPLAY_FORECAST_MONTH;
    state.yearMonthInfoCreatedForecast = DateService.getBeforeYearMonth(DateService.convertDateToIYearMonth(new Date())); // デフォルトは先月
    state.isOpenSelectInputType = undefined;
    state.coordinateFilterButton = {
      x: 0,
      y: 0,
    };
    state.downloadForecastModalState = {
      modalState: false,
      period: {
        start: { year: 0, month: 0 },
        end: { year: 0, month: 0 },
      },
      startMonthPickerState: false,
      endSelectInputState: false,
      startInvalidMessageState: false,
      loadingState: {
        status: {
          type: 'none',
        },
      },
    };
    state.editForecastModalState = {
      modalState: false,
      selectedStaffIdList: [],
    };
  };

  /**
   * 初期化処理
   */
  const init = async () => {
    $reset();
    updateDisplayBusinesses();
  };

  /**
   * テーブルの表示内容を更新するために、必要な情報のみリセットする
   */
  const resetTableContents = () => {
    state.businessTableContents.contentHeader = {} as TableContentHeader;
    state.businessTableContents.contentItems = [];
    state.businessTableContents.checkedDisplayIds = [];
    state.businessTableContents.loadingState = {
      status: {
        type: 'none',
      },
    };
  };

  /**
   * テーブルのヘッダー情報を取得する
   * @param forecastCount - 表示する内示の数
   * @returns ヘッダー情報
   */
  const fetchTableContentHeader = (forecastCount: number): TableContentHeader => {
    const forecasts: TableContentProperty[] = [];
    for (let i = 0; i < forecastCount; i++) {
      forecasts.push({
        key: `supplierAnnounce-${i + 1}`,
        isVariableWidth: true,
        isNarrowable: true,
        isEditable: false,
        sortOption: {
          isSortable: true,
          isAsc: true,
        },
        propertyPath: { paths: [], propertyType: 'number' },
      });
    }
    return {
      contentProperties: [
        {
          key: 'choice',
          isVariableWidth: false,
          isNarrowable: false,
          isEditable: false,
          sortOption: { isSortable: false, isAsc: true },
          headerWidth: 32,
          propertyPath: { paths: ['choice'], propertyType: 'string' },
        },
        {
          key: 'expansion',
          isVariableWidth: false,
          isNarrowable: false,
          isEditable: false,
          sortOption: { isSortable: false, isAsc: true },
          headerWidth: 32,
          propertyPath: { paths: ['expansion'], propertyType: 'string' },
        },
        {
          key: 'supplierForecastStaff',
          isVariableWidth: true,
          isNarrowable: true,
          isEditable: false,
          sortOption: {
            isSortable: true,
            isAsc: true,
          },
          propertyPath: { paths: ['supplierForecastStaff'], propertyType: 'string' },
        },
        {
          key: 'businessId',
          isVariableWidth: true,
          isNarrowable: true,
          isEditable: false,
          sortOption: {
            isSortable: true,
            isAsc: true,
          },
          propertyPath: { paths: ['businessId'], propertyType: 'string' },
        },
        {
          key: 'supplierNumber',
          isVariableWidth: true,
          isNarrowable: true,
          isEditable: false,
          sortOption: {
            isSortable: true,
            isAsc: true,
          },
          propertyPath: { paths: ['supplierNumber'], propertyType: 'string' },
        },
        {
          key: 'supplierName',
          isVariableWidth: true,
          isNarrowable: true,
          isEditable: false,
          sortOption: {
            isSortable: true,
            isAsc: true,
          },
          propertyPath: { paths: ['supplierName'], propertyType: 'string' },
        },
        {
          key: 'manufacturer',
          isVariableWidth: true,
          isNarrowable: true,
          isEditable: false,
          sortOption: {
            isSortable: true,
            isAsc: true,
          },
          propertyPath: { paths: ['manufacturer'], propertyType: 'string' },
        },
        {
          key: 'businessNumber',
          isVariableWidth: true,
          isNarrowable: true,
          isEditable: false,
          sortOption: {
            isSortable: true,
            isAsc: true,
          },
          propertyPath: { paths: ['businessNumber'], propertyType: 'string' },
        },
        {
          key: 'businessName',
          isVariableWidth: true,
          isNarrowable: true,
          isEditable: false,
          sortOption: {
            isSortable: true,
            isAsc: true,
          },
          propertyPath: { paths: ['businessName'], propertyType: 'string' },
        },
        {
          key: 'package',
          isVariableWidth: true,
          isNarrowable: true,
          isEditable: false,
          sortOption: {
            isSortable: true,
            isAsc: true,
          },
          propertyPath: { paths: ['package'], propertyType: 'number' },
        },
        {
          key: 'unit',
          isVariableWidth: true,
          isNarrowable: true,
          isEditable: false,
          sortOption: {
            isSortable: true,
            isAsc: true,
          },
          propertyPath: { paths: ['unit'], propertyType: 'string' },
        },
        {
          key: 'warehouseNumber',
          isVariableWidth: true,
          isNarrowable: true,
          isEditable: false,
          sortOption: {
            isSortable: true,
            isAsc: true,
          },
          propertyPath: { paths: ['warehouseNumber'], propertyType: 'string' },
        },
        {
          key: 'warehouse',
          isVariableWidth: true,
          isNarrowable: true,
          isEditable: false,
          sortOption: {
            isSortable: true,
            isAsc: true,
          },
          propertyPath: { paths: ['warehouse'], propertyType: 'string' },
        },
        // STOPSHIP: baseTableContentの展開機能を実装したら、以下のコメントを外す
        // {
        //   key: 'customerNumber',
        //   isVariableWidth: true,
        //   isNarrowable: true,
        //   isEditable: false,
        //   sortOption: {
        //     isSortable: true,
        //     isAsc: true,
        //   },
        //   propertyPath: { paths: ['customerNumber'], propertyType: 'string' },
        // },
        // {
        //   key: 'customerName',
        //   isVariableWidth: true,
        //   isNarrowable: true,
        //   isEditable: false,
        //   sortOption: {
        //     isSortable: true,
        //     isAsc: true,
        //   },
        //   propertyPath: { paths: ['customerName'], propertyType: 'string' },
        // },
        // {
        //   key: 'salesDestinationNumber',
        //   isVariableWidth: true,
        //   isNarrowable: true,
        //   isEditable: false,
        //   sortOption: {
        //     isSortable: true,
        //     isAsc: true,
        //   },
        //   propertyPath: { paths: ['salesDestinationNumber'], propertyType: 'string' },
        // },
        // {
        //   key: 'salesDestinationName',
        //   isVariableWidth: true,
        //   isNarrowable: true,
        //   isEditable: false,
        //   sortOption: {
        //     isSortable: true,
        //     isAsc: true,
        //   },
        //   propertyPath: { paths: ['salesDestinationName'], propertyType: 'string' },
        // },
        {
          key: 'supplierForecastUpdateAt',
          isVariableWidth: true,
          isNarrowable: true,
          isEditable: false,
          sortOption: {
            isSortable: true,
            isAsc: true,
          },
          propertyPath: { paths: ['ForecastForReceiptReceiveDate'], propertyType: 'string' },
        },
        {
          key: 'Space8px',
          isVariableWidth: false,
          isNarrowable: false,
          isEditable: false,
          sortOption: {
            isSortable: false,
            isAsc: true,
          },
          headerWidth: 8,
          propertyPath: { paths: [], propertyType: 'string' },
        },
        ...forecasts,
        HEADER_MODIFY_TABLE_CONTENT_PROPERTY,
      ],
      options: {
        stableColumnCount: 2,
        resizableHeaderColumn: true,
      },
      forecastStartYearMonth: DateService.getShiftedIYearMonthByMonths(
        state.yearMonthInfoCreatedForecast.year,
        state.yearMonthInfoCreatedForecast.month,
        1
      ), // 作成月の翌月から表示
    };
  };

  /**
   * 取引を取得する
   */
  const fetchDisplayBusinesses = async () => {
    state.businessTableContents.loadingState = {
      status: {
        type: 'loading',
      },
    };

    if (state.yearMonthInfoCreatedForecast.year === 0 || state.yearMonthInfoCreatedForecast.month === 0) return;

    await initTableFilter();

    // 永続化された設定を読み込む
    await loadPreferences();
    const savedPreferences = myPreferences[SUUPLIER_FORRECAST_ALL_BUSINESS_TABLE_TYPE];

    if (savedPreferences) {
      // ソート設定の適用
      if (savedPreferences.sortKey) {
        state.businessTableContents.sortKey = savedPreferences.sortKey;
      }
      if (savedPreferences.isAsc !== undefined) {
        state.businessTableContents.isAsc = savedPreferences.isAsc;
      }

      // ページサイズ設定の適用
      if (savedPreferences.pageSize) {
        if (state.businessTableContents.pagingOptions) {
          state.businessTableContents.pagingOptions.pageSize = savedPreferences.pageSize;
        } else {
          state.businessTableContents.pagingOptions = {
            ...initPagingOptions(savedPreferences.pageSize),
            pageSize: savedPreferences.pageSize,
          };
        }
      }
    }

    // UIのソート表示を更新
    if (state.businessTableContents.sortKey) {
      state.businessTableContents.contentHeader.contentProperties = state.businessTableContents.contentHeader.contentProperties.map(
        (property) => {
          const currentIsAsc = state.businessTableContents.isAsc ?? true;
          property.sortOption.isAsc = property.key === state.businessTableContents.sortKey ? currentIsAsc : true;
          return property;
        }
      );
    }

    const result = await getAllSupplierForecastUseCase.Execute(
      supplierForecastRepository,
      state.yearMonthInfoCreatedForecast,
      state.businessTableContents.pagingOptions,
      state.businessTableContents.sortKey,
      state.businessTableContents.isAsc,
      cachedFilterItems.value
    );

    switch (result.status.type) {
      case 'success':
        state.businessTableContents.contentItems = result.status.data.result;
        state.businessTableContents.pagingOptions = result.status.data.pagingOptions;
        if ((result.status.data.pagingOptions?.returnCount ?? 0) === 0) {
          state.businessTableContents.loadingState.status.type = 'zeroMatch';
        } else {
          state.businessTableContents.loadingState.status.type = 'success';
        }
        console.log({ success: state.businessTableContents.contentItems });
        break;
      case 'error':
        state.businessTableContents.loadingState = {
          status: {
            type: 'error',
            errorMessage: t('データ取得に失敗しました'),
          },
        };
        console.log('error');
        break;
    }
  };

  /**
   * テーブルに表示する取引情報を更新する
   */
  const updateDisplayBusinesses = async () => {
    resetTableContents();
    // 受領用内示が選択されている場合、ヘッダーの更新と取引情報の取得を行う
    state.businessTableContents.contentHeader = fetchTableContentHeader(state.monthsDisplayForecast);
    await fetchDisplayBusinesses();
    state.businessTableContents.renderingKey += 1;
  };

  /**
   * フィルターモーダルの適用ボタンクリック時の処理
   * @param filterItems - フィルターモーダル内で選択された項目
   */
  const onAppliedFilterModal = async (filterItems: FilterContentItem[]) => {
    // フィルターモーダルの選択情報を更新
    await bulkUpdateFilterInfos(simpleCopy(filterItems));
    // 表示内容を更新
    await updateDisplayBusinesses();
  };

  /**
   * 内示作成月選択時の処理
   * @param selectedMonths - 選択された月
   */
  const onSelectedCreatedMonth = async (selectedMonths: IYearMonth) => {
    state.yearMonthInfoCreatedForecast = selectedMonths;
    await updateDisplayBusinesses();
  };

  /**
   * 内示を表示する月数を選択した際の処理
   * @param selectedIndex - 選択されたインデックス
   */
  const onSelectedMonthsDisplayForecast = async (selectedIndex?: number) => {
    if (selectedIndex === undefined) return;
    state.monthsDisplayForecast = selectedIndex + MIN_OF_DISPLAY_FORECAST_MONTH;
    // 内示の列数が変わるためヘッダーも更新
    await updateDisplayBusinesses();
  };

  /**
   * ページング変更時の処理
   * @param pagingOptions - ページングオプション
   */
  const onChangePaging = async (pagingOptions: IPagingOptions) => {
    state.businessTableContents.pagingOptions = {
      pageSize: pagingOptions.pageSize,
      offset: pagingOptions.offset,
      totalResultsAvailable: pagingOptions.totalResultsAvailable,
      returnCount: pagingOptions.returnCount,
    };

    // ページサイズ設定の永続化対応：IndexedDBに保存
    await loadPreferences();
    if (!myPreferences[SUUPLIER_FORRECAST_ALL_BUSINESS_TABLE_TYPE]) {
      myPreferences[SUUPLIER_FORRECAST_ALL_BUSINESS_TABLE_TYPE] = {};
    }
    myPreferences[SUUPLIER_FORRECAST_ALL_BUSINESS_TABLE_TYPE].pageSize = state.businessTableContents.pagingOptions.pageSize;
    await savePreferences();

    await fetchDisplayBusinesses();
  };

  /**
   * テーブルのデータをソートする
   * @param sortKey - ソートキー
   */
  const sortTableContentItems = async (sortKey: TableContentProperty) => {
    const newIsAsc = sortKey.key === state.businessTableContents.sortKey ? !state.businessTableContents.isAsc : false;
    const formattedKey = (() => {
      if (sortKey.key === 'supplierForecastUpdateAt') {
        return 'updateAt';
      } else if (sortKey.key === 'supplierForecastStaff') {
        return 'aggregateBusinessResponsibleUser';
      } else {
        return sortKey.key;
      }
    })();
    state.businessTableContents.sortKey = formattedKey;
    state.businessTableContents.isAsc = newIsAsc;

    // ソート設定の永続化対応：IndexedDBに保存
    await loadPreferences();
    if (!myPreferences[SUUPLIER_FORRECAST_ALL_BUSINESS_TABLE_TYPE]) {
      myPreferences[SUUPLIER_FORRECAST_ALL_BUSINESS_TABLE_TYPE] = {};
    }
    myPreferences[SUUPLIER_FORRECAST_ALL_BUSINESS_TABLE_TYPE].sortKey = state.businessTableContents.sortKey;
    myPreferences[SUUPLIER_FORRECAST_ALL_BUSINESS_TABLE_TYPE].isAsc = state.businessTableContents.isAsc;
    await savePreferences();

    state.businessTableContents.contentHeader.contentProperties = state.businessTableContents.contentHeader.contentProperties.map(
      (property) => {
        property.sortOption.isAsc = property.key === sortKey.key ? newIsAsc : true;
        return property;
      }
    );
    state.businessTableContents.pagingOptions.offset = 0;
    fetchDisplayBusinesses();
  };

  /**
   * テーブルのデータをフィルターする
   * @param narrowState - 絞り込み状態
   */
  const filterTableContentItems = (narrowState: NarrowState[]) => {
    state.businessTableContents.filterState = narrowState;
    fetchDisplayBusinesses();
  };

  // MARK: ↓ ダウンロード関連
  /**
   * ダウンロードモーダルを開く処理
   */
  const onClickDownloadButton = () => {
    // 期間の初期化
    // 開始は作成月
    state.downloadForecastModalState.period.start = state.yearMonthInfoCreatedForecast;
    // 終了は作成月+表示月数
    const { updateYear: year, updateMonth: month } = DateService.updateYearMonth(
      state.yearMonthInfoCreatedForecast.year,
      state.yearMonthInfoCreatedForecast.month,
      state.monthsDisplayForecast
    );
    state.downloadForecastModalState.period.end = { year, month };
    state.downloadForecastModalState.modalState = true;
  };

  /**
   * ダウンロードモーダルのダウンロードボタンのdisabled判定
   */
  const isDisabledOpenDownloadModal = computed(() => {
    return state.businessTableContents.contentItems === undefined || state.businessTableContents.contentItems.length === 0;
  });

  /**
   * ダウンロードボタンのdisabled判定
   */
  const isDisabledDownloadButton = computed(() => {
    return !state.downloadForecastModalState.period.start || !state.downloadForecastModalState.period.end;
  });

  /**
   * ファイル選択ボタン押下
   */
  const onClickSelectFileButton = () => {
    // ファイル選択を発火させる
    document.getElementById('file-input')?.click();
  };

  /**
   * ファイル選択
   * @param event - イベント
   */
  const onSelectedFile = (event: Event) => {
    if (!(event.target instanceof HTMLInputElement)) return [];
    const file = event.target.files?.[0];
    if (!file) return [];
  };

  /**
   * 開始年月ピッカーの表示切り替え
   * @param isOpen - 表示状態
   */
  const toggleStartMonthPicker = (isOpen: boolean) => {
    state.downloadForecastModalState.startMonthPickerState = isOpen;
    // 終了年月との排他処理
    if (isOpen) {
      state.downloadForecastModalState.endSelectInputState = false;
      state.isOpenSelectInputType = undefined;
    }
  };

  /**
   * 終了年月セレクトの表示切り替え
   * @param isOpen - 表示状態
   */
  const toggleEndSelectInput = (isOpen: boolean) => {
    state.downloadForecastModalState.endSelectInputState = isOpen;
    state.isOpenSelectInputType = 'DISPLAY_YEAR_MONTH';
    // 開始年月との排他処理
    if (isOpen) {
      state.downloadForecastModalState.startMonthPickerState = false;
    }
  };

  /**
   * 開始年月選択
   * @param start - 開始年月
   */
  const onSelectedStartPeriod = (start: IYearMonth) => {
    // 無効な範囲が選択された場合はエラーメッセージを表示し、変更を回避する
    if (viewModel.isInvalidStartPeriod(start)) {
      state.downloadForecastModalState.startInvalidMessageState = true;
      return;
    }
    state.downloadForecastModalState.period.start = start;
    state.downloadForecastModalState.startInvalidMessageState = false;
  };

  /**
   * 終了年月選択
   * @param selectedIndex - 選択されたインデックス
   */
  const onSelectedEndPeriod = (selectedIndex: number) => {
    state.downloadForecastModalState.period.end = viewModel.getValueByTargetYearMonthList(
      selectedIndex,
      state.downloadForecastModalState.period.start
    );
  };

  /**
   * 終了年月のラベルを取得
   */
  const getEndPeriodLabel = computed(() => {
    return viewModel.getEndPeriodLabelDownloadModal(state.downloadForecastModalState.period.end);
  });

  /**
   * 終了年月の選択肢を取得
   */
  const getYearMonthListForEndPeriod = computed(() => {
    return viewModel.getEndPeriodOptionsDownloadModal(state.downloadForecastModalState.period.start);
  });

  /**
   * ダウンロードボタン押下
   */
  const onApplyDownload = async () => {
    // モーダルを閉じる
    state.downloadForecastModalState.modalState = false;
    // ローディング状態にする
    state.downloadForecastModalState.loadingState.status.type = 'loading';

    const exportData = await _fetchExportData();
    if (exportData instanceof Error) {
      // ローディング状態を解除
      state.downloadForecastModalState.loadingState.status.type = 'error';
      // エラートースト表示
      toast.error(t('ダウンロード:失敗メッセージ'));
    } else {
      // ダウンロード処理
      const downloadResult = await _exportSupplierForecast(exportData);
      if (downloadResult instanceof Error) {
        // ローディング状態を解除
        state.downloadForecastModalState.loadingState.status.type = 'error';
        // エラートースト表示
        toast.error(t('ダウンロード:失敗メッセージ'));
      } else {
        // ローディング状態を解除
        state.downloadForecastModalState.loadingState.status.type = 'success';
        // 成功トースト表示
        toast.success(t('ダウンロード:成功メッセージ'));
      }
    }
  };

  /**
   * ダウンロードに必要なデータを取得する
   */
  const _fetchExportData = async (): Promise<ISupplierForecastExportDataResult | Error> => {
    if (!state.downloadForecastModalState.period.start || !state.downloadForecastModalState.period.end)
      return new Error(t('ダウンロード:失敗メッセージ'));

    const useCase = new BulkGetSupplierForecastExportData();

    const result = await useCase.Execute(
      supplierForecastRepository,
      state.downloadForecastModalState.period.start,
      state.downloadForecastModalState.period.end,
      cachedFilterItems.value
    );
    if (result.status.type === 'success') {
      // エクセル情報がundefinedの場合はエラーを返す
      if (!result.status.data.supplierForecastTemplateExcelData) {
        return new Error(t('ダウンロード:テンプレートファイルが取得できていません'));
      }
      return result.status.data;
    } else {
      return new Error(t('ダウンロード:失敗メッセージ'));
    }
  };

  /**
   * 仕入先内示出力
   * @param exportData - 出力データ
   */
  const _exportSupplierForecast = async (exportData: ISupplierForecastExportDataResult): Promise<void | Error> => {
    if (!state.downloadForecastModalState.period.start || !state.downloadForecastModalState.period.end) return;

    // TODO: loadingStateをテーブル用とダウンロード用で分けるべき
    state.businessTableContents.loadingState = {
      status: {
        type: 'loading',
      },
    };

    const result = await DownloadForecastUseCase.createSupplierAnnounceExcelForExport(
      exportData.supplierForecastTemplateExcelData,
      exportData.data,
      state.downloadForecastModalState.period.start,
      state.downloadForecastModalState.period.end
    );
    switch (result.status.type) {
      case 'success':
        // TODO: loadingStateをテーブル用とダウンロード用で分けるべき
        state.businessTableContents.loadingState = {
          status: {
            type: 'success',
          },
        };
        await saveAs(result.status.data, `${t('システム出力仕入れ先内示', { date: DateService.getTodayString() })}.xlsx`);
        return;
      case 'error':
        // TODO: loadingStateをテーブル用とダウンロード用で分けるべき
        state.businessTableContents.loadingState = {
          status: {
            type: 'success',
          },
        };
        return result.status.error;
    }
  };

  // MARK: ↑ ダウンロード関連

  // MARK: ↓ 編集関連

  /**
   * 編集モーダルを開く処理
   */
  const onClickEditButton = () => {
    // 編集モーダルを開く
    state.editForecastModalState.modalState = true;
  };

  /**
   * 編集ボタンのdisabled判定
   */
  const isDisabledEditButton = computed(() => {
    return !state.businessTableContents.checkedDisplayIds.length;
  });

  /**
   * 編集モーダルの適用ボタンのdisabled判定
   */
  const isDisabledApplyEditButton = computed(() => {
    return !state.editForecastModalState.selectedStaffIdList.length;
  });

  /**
   * 編集モーダルの適用ボタンクリック時の処理
   */
  const onApplyEdit = async () => {
    // モーダルを閉じる
    state.editForecastModalState.modalState = false;
    // 編集処理
    const updateResult = await _updateResponsibleUserId(state.editForecastModalState.selectedStaffIdList);
    // チェックボックスを外す
    state.businessTableContents.checkedDisplayIds = [];
    state.businessTableContents.renderingKey += 1;
    if (updateResult instanceof Error) {
      // エラートースト表示
      toast.error(t('担当者編集:失敗メッセージ'));
      return;
    } else {
      // 成功トースト表示
      toast.success(t('担当者編集:成功メッセージ'));
    }
    await updateDisplayBusinesses();
  };

  /**
   * 仕入先内示の担当者IDを更新する
   * @param selectedStaffIdList - 選択された担当者IDリスト
   */
  const _updateResponsibleUserId = async (selectedStaffIdList: string[]): Promise<void | Error> => {
    state.businessTableContents.loadingState = {
      status: {
        type: 'loading',
      },
    };

    const result = await bulkUpdateSupplierForecastUseCase.Execute(
      supplierForecastRepository,
      checkedBusinessIds.value,
      selectedStaffIdList
    );

    switch (result.status.type) {
      case 'success':
        state.businessTableContents.loadingState = {
          status: {
            type: 'success',
          },
        };

        return;
      case 'error':
        state.businessTableContents.loadingState = {
          status: {
            type: 'error',
            errorMessage: t(result.status.error.message),
          },
        };
        return result.status.error;
    }
  };

  // MARK: ↑ 編集関連

  return {
    monthsOptionsDisplayForecastNumber,
    state,
    checkedBusinessIds,
    $reset,
    init,
    resetTableContents,
    fetchTableContentHeader,
    fetchDisplayBusinesses,
    updateDisplayBusinesses,
    onAppliedFilterModal,
    onSelectedCreatedMonth,
    onSelectedMonthsDisplayForecast,
    onChangePaging,
    sortTableContentItems,
    filterTableContentItems,
    onClickDownloadButton,
    isDisabledOpenDownloadModal,
    isDisabledDownloadButton,
    onClickSelectFileButton,
    onSelectedFile,
    toggleStartMonthPicker,
    toggleEndSelectInput,
    onSelectedStartPeriod,
    onSelectedEndPeriod,
    getEndPeriodLabel,
    getYearMonthListForEndPeriod,
    onApplyDownload,
    onClickEditButton,
    isDisabledEditButton,
    isDisabledApplyEditButton,
    onApplyEdit,
    getFilterItemsValue,
  };
});

/**
 * AllBusinessesForSupplierForecastStoreの型
 */
export type AllBusinessesForSupplierForecastStore = ReturnType<typeof useAllBusinessesForSupplierForecastStore>;
